import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of, switchMap, take } from 'rxjs';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { ProsellContextService } from '../services/prosell-context.service';

/**
 * Angular evalúa los `canActivate` de una misma ruta en paralelo, no en secuencia — así que este
 * guard NO puede asumir que `permissionGuard` (que corre a la vez) ya dejó listos ni el perfil ni
 * el sidebar. `ctx.canBuy()`/`canSell()` dependen de DOS cosas cargadas de forma asíncrona:
 * `usuarioService.perfil()` (para el roleId) y `permissionService.sidebarCache` (para los permisos
 * de ese rol) — en una carga en frío (pestaña nueva, F5) ambas están vacías en el instante exacto
 * en que este guard corre, así que un chequeo síncrono contra esos signals falla siempre la primera
 * vez, sin importar el permiso real del rol (bug raíz del "Acceso denegado" en /prosell/comprar al
 * abrir "Ver tienda" en pestaña nueva). `getPerfil()`/`getSidebarForRoleCached()` sin forzar
 * reutilizan el caché si ya está cargado, así que esto no agrega peticiones HTTP extra en la
 * navegación normal dentro de la SPA.
 */
function esperarPerfilYValidar(
  puedeAcceder: (ctx: ProsellContextService) => boolean,
  router: Router,
  ownerAccede = false
): Observable<boolean | UrlTree> {
  const ctx = inject(ProsellContextService);
  const usuarioService = inject(UsuarioService);
  const permissionService = inject(PermissionService);
  const redirigir = () => router.createUrlTree(['/prosell']);

  return usuarioService.getPerfil().pipe(
    take(1),
    switchMap(perfil => {
      // OWNER tiene acceso implícito: no tiene filas de permiso de rol, así que `canSell()` da false.
      if (ownerAccede && perfil?.isOwner === true) {
        return of(true);
      }
      const roleId = perfil?.role?.id;
      if (!roleId) {
        return of(redirigir());
      }
      return permissionService.getSidebarForRoleCached(roleId).pipe(
        take(1),
        map(() => (puedeAcceder(ctx) ? true : redirigir()))
      );
    }),
    catchError(() => of(redirigir()))
  );
}

/** Bloquea rutas de vendedor si el rol no tiene permiso en productos-maestro (el OWNER siempre accede). */
export const prosellSellerGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  return esperarPerfilYValidar(ctx => ctx.canSell(), router, true);
};

/** Bloquea rutas de comprador si el rol no tiene permiso en comprar. */
export const prosellBuyerGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  return esperarPerfilYValidar(ctx => ctx.canBuy(), router);
};
